import React from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { Stack, Text, Header, Button, Loader } from '@crossfit/ui'
import { Invitation } from 'types'
import Modal from 'components/Modal'
import * as affiliatesService from 'services/affiliates'
import { useNotification } from 'components/Notification'
import { useDispatch, useSelector } from 'react-redux'
import { getAffiliate } from 'store/selectors'
import { AppDispatch } from 'store'
import { fetchAffiliateInvitationsThunk } from 'store/slices/affiliate'

type Props = {
  onClose: () => void
  invitation: Invitation
}

function useRevokeInvite({ onClose, invitation }: Props) {
  const affiliate = useSelector(getAffiliate)
  const { token, email } = invitation
  const form = useForm()
  const queryClient = useQueryClient()
  const showNotification = useNotification()
  const dispatch = useDispatch() as AppDispatch

  const revertToCoach = useMutation(() =>
    affiliatesService.revokeInvite({
      token,
      affiliateId: affiliate?.id,
    }),
  )

  async function handleSubmit() {
    await revertToCoach.mutateAsync()
    await queryClient.refetchQueries('invitations')
    dispatch(fetchAffiliateInvitationsThunk(affiliate?.id))
    showNotification(`The invite to ${email} has been revoked.`)
    onClose()
  }

  return {
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: form.formState.isSubmitting,
  }
}

function RevokeInvite({ onClose, invitation }: Props) {
  const { name } = invitation
  const { onSubmit, isSubmitting } = useRevokeInvite({ onClose, invitation })
  const affiliate = useSelector(getAffiliate)

  return (
    <Modal
      onClose={onClose}
      header={
        <Header size='small' as='h3'>
          Revoke {name}’s Invite?
        </Header>
      }
    >
      {isSubmitting ? (
        <Loader />
      ) : (
        <form onSubmit={onSubmit}>
          <Text mb={8}>
            <strong>{name}</strong> will no longer be able to accept an invite
            to join the {affiliate?.name} team.
          </Text>
          <Stack
            direction='horizontal'
            style={{ justifyContent: 'flex-end' }}
            spacing={4}
          >
            <Button type='button' onClick={onClose} variant='secondary'>
              Cancel
            </Button>
            <Button type='submit' variant='destructive'>
              Revoke Invite
            </Button>
          </Stack>
        </form>
      )}
    </Modal>
  )
}

export default RevokeInvite
